import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Col, Row, Spin } from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Company } from 'Types/CompaniesTypes';
import CompanyCardLarge from 'Components/common/components/CompanyCardLarge';
import SmallButton from 'Components/common/components/SmallButton';
import CompanySkeleton from 'Components/common/skeleton/CompanySkeleton';
import { PeopleNotFound } from 'Pages/people/PeopleGroupDetail';
import { actions as companiesActions } from 'Slice/CompaniesSlice';
import { isMappedSubsidiary } from 'Utils/subsidiaryUtils';
import { SUBSIDIARY_PROCESSING_TEXT, SUBSIDIARY_REQUEST_INFO_TEXT } from 'Utils/Constants';
import {
  maxWidth, minWidthApp, retina, wMedium,
} from 'Utils/utilsStyle';

const Wrapper = styled.div`
  .subsidiaries-scroll {
    overflow-x: hidden !important;
  }
  .Grid-width-row {
    margin: 0 !important;
    .subsidiary-card-col {
      margin-bottom: 12px;
    }
  }
  /* Unmapped subsidiaries have no xiQ Account Detail: card is display-only,
     only the Request Company overlay stays interactive */
  .unmapped-subsidiary > * {
    pointer-events: none;
  }
    /* Own stacking context: the overlay's z-index stays scoped to the card, so sticky
     headers (e.g. the cluster tabs bar, z-index 1) still paint above it on scroll */
  .unmapped-subsidiary {
    position: relative;
    z-index: 0;
  }
  .unmapped-subsidiary .logo-overlay {
    pointer-events: auto;
    visibility: hidden;
    padding: 6px;
    .request-company-action {
      display: flex;
      align-items: center;
      justify-content: center;
      max-width: 100%;
    }
    /* Scales with the logo box so the button never overflows the card */
    .small-button-chinese {
      max-width: 100%;
      font-size: 14px;
      font-weight: ${wMedium};
      line-height: 16px;
      padding: 8px 16px;
      white-space: nowrap;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 12px;
        line-height: 14px;
        padding: 6px 12px;
      }
      @media all and (max-width: ${minWidthApp}) {
        font-size: 10px;
        line-height: 12px;
        padding: 5px 8px;
        white-space: normal;
      }
    }
  }
  .unmapped-subsidiary:hover .logo-overlay,
  .request-company-action.is-loading {
    visibility: visible;
  }
`;

type Props = {
  companies: Company[];
  loading: boolean;
  ended?: boolean;
  onLoadMore?: () => void;
  // false renders a plain grid (caller controls loading), true uses infinite scroll
  infinite?: boolean;
  // id of a scrollable parent to paginate on; window scroll when omitted
  scrollableTarget?: string;
  emptyText?: string;
};

const SKELETON_COUNT = 6;

// canRequestData = 1 and not yet requested by this user: offer Request Company on hover
const canRequestSubsidiary = (company: Company) => company.canRequestData === 1
  && company.dataRequested !== 1
  && !!company.subsidiaryItemId;

const getSkeletons = () => Array.from({ length: SKELETON_COUNT }, (_, i) => (
  <Col key={`subsidiary-skeleton-${i}`} xxl={{ span: 4 }} xl={{ span: 4 }} lg={{ span: 6 }} className="gutter-row">
    <CompanySkeleton />
  </Col>
));

const SubsidiariesGrid = function SubsidiariesGrid(props: Props) {
  const {
    companies, loading, ended, onLoadMore, infinite, emptyText, scrollableTarget,
  } = props;
  const dispatch = useDispatch();

  if (!loading && !companies.length) {
    return <PeopleNotFound>{emptyText}</PeopleNotFound>;
  }

  const handleRequest = (e: React.MouseEvent<HTMLButtonElement>, subsidiaryItemId: number) => {
    e.stopPropagation();
    dispatch(companiesActions.requestSubsidiaryData({ subsidiaryItemId }));
  };

  const grid = (
    <Row gutter={[24, 24]} className="Grid-width-row">
      {companies.map((company, index) => {
        const mapped = isMappedSubsidiary(company);
        const requestOverlay = !mapped && canRequestSubsidiary(company) ? (
          <span className={company.requestLoading ? 'request-company-action is-loading' : 'request-company-action'}>
            {company.requestLoading ? <Spin /> : (
              <SmallButton
                title="Request Company"
                borderRadius="6px"
                onButtonClick={(e) => handleRequest(e, company.subsidiaryItemId as number)}
              />
            )}
          </span>
        ) : null;
        let infoText = '';
        if (!mapped) {
          infoText = canRequestSubsidiary(company)
            ? SUBSIDIARY_REQUEST_INFO_TEXT
            : SUBSIDIARY_PROCESSING_TEXT;
        }
        return (
          <Col
            key={mapped ? company.companyId : `${company.subsidiaryItemId ?? company.companyName}-${index}`}
            xxl={{ span: 4 }}
            xl={{ span: 4 }}
            lg={{ span: 6 }}
            className="gutter-row subsidiary-card-col"
          >
            <div className={mapped ? undefined : 'unmapped-subsidiary'}>
              <CompanyCardLarge
                companyData={company}
                showButton={mapped}
                disableNavigation={!mapped}
                showDrawerIcon={!!company.companyNotes}
                showSapIcon={!!company.accountPlanAllowed}
                logoOverlay={requestOverlay}
                infoText={infoText}
              />
            </div>
          </Col>
        );
      })}
      {loading ? getSkeletons() : null}
    </Row>
  );

  if (!infinite) return <Wrapper>{grid}</Wrapper>;

  const handleNext = () => {
    if (!loading && companies.length && onLoadMore) onLoadMore();
  };

  return (
    <Wrapper>
      <InfiniteScroll
        dataLength={companies.length}
        next={handleNext}
        hasMore={!ended}
        loader={null}
        scrollableTarget={scrollableTarget}
        className="o-x-hidden subsidiaries-scroll"
      >
        {grid}
      </InfiniteScroll>
    </Wrapper>
  );
};

SubsidiariesGrid.defaultProps = {
  ended: true,
  onLoadMore: undefined,
  infinite: true,
  scrollableTarget: undefined,
  emptyText: 'No Subsidiaries available.',
};

export default React.memo(SubsidiariesGrid);
